import { EmployeeRowData, ActualHoursEmployeeRowData } from '../types';

/**
 * Check if a split role has child split roles
 */
export const hasChildSplitRoles = (splitRoleId: string, employeeRows: EmployeeRowData[]): boolean => {
  return employeeRows.some((row: EmployeeRowData) =>
    row.isChildRow && row.parentId === splitRoleId && row.id !== splitRoleId
  );
};

/**
 * Check if an actual split role has child split roles
 */
export const hasActualChildSplitRoles = (splitRoleId: string, actualEmployeeRows: ActualHoursEmployeeRowData[]): boolean => {
  return actualEmployeeRows.some((row: ActualHoursEmployeeRowData) =>
    row.isChildRow && row.parentId === splitRoleId && row.id !== splitRoleId
  );
};

/**
 * Calculate if the Split Role button should be enabled
 */
export const calculateCanSplitRole = (
  tabState: { activeTab: 'schedule' | 'actual' },
  employeeRows: EmployeeRowData[],
  actualEmployeeRows: ActualHoursEmployeeRowData[]
): boolean => {
  // Use context-aware data based on active tab
  const currentEmployeeRows = tabState.activeTab === 'actual' ? actualEmployeeRows : employeeRows;

  // Check if Split Role button should be enabled
  // Enabled when: single row, OR parent+child(ren) selected together
  const selectedEmployees = currentEmployeeRows.filter((row: EmployeeRowData | ActualHoursEmployeeRowData) => row.isSelected);

  let canSplitRole = false;

  if (selectedEmployees.length === 1) {
    // Single row selected (parent or child)
    const selectedEmployee = selectedEmployees[0];

    if (!selectedEmployee.isChildRow) {
      // Single parent row selected: always allow splitting
      canSplitRole = true;
    } else {
      // Single child row selected: allow splitting if it can create new sibling
      if (tabState.activeTab === 'actual') {
        canSplitRole = !!selectedEmployee.parentId && !hasActualChildSplitRoles(selectedEmployee.id, actualEmployeeRows);
      } else {
        canSplitRole = !!selectedEmployee.parentId && !hasChildSplitRoles(selectedEmployee.id, employeeRows);
      }
    }
  } else if (selectedEmployees.length > 1) {
    // Multiple rows selected: check if it's a parent + its child(ren) pattern
    const parentRows = selectedEmployees.filter((row: EmployeeRowData | ActualHoursEmployeeRowData) => !row.isChildRow);
    const childRows = selectedEmployees.filter((row: EmployeeRowData | ActualHoursEmployeeRowData) => row.isChildRow);

    // Enable if exactly 1 parent is selected AND all selected children belong to that parent
    if (parentRows.length === 1 && childRows.length > 0) {
      const parentId = parentRows[0].id;
      const allChildrenBelongToParent = childRows.every((child: EmployeeRowData | ActualHoursEmployeeRowData) => child.parentId === parentId);
      canSplitRole = allChildrenBelongToParent;
    }
  }
  // Note: canSplitRole remains false for no selection or invalid multi-selection patterns

  return canSplitRole;
};